import { Check, Search, X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * FilterBar / FilterField (Playbook § Padrões de casa).
 *
 * Uma barra só, campos alinhados pela base, sem moldura. O rótulo é 12px bold
 * na cor do texto — não cinza caixa-alta — e continua visível (Hard rule 4).
 */

export function FilterBar({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-end gap-4', className)} role="search">
      {children}
    </div>
  );
}

export function FilterBusca({
  id,
  label,
  valor,
  aoMudar,
  placeholder,
  className,
}: {
  id: string;
  label: string;
  valor: string;
  aoMudar: (valor: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <Field id={id} label={label} className={cn('min-w-56 max-w-md flex-1', className)}>
      {(props) => (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
          <Input
            {...props}
            type="search"
            value={valor}
            placeholder={placeholder}
            onChange={(evento) => aoMudar(evento.target.value)}
            className="pl-9"
          />
        </div>
      )}
    </Field>
  );
}

const TODOS = '__todos__';

export function FilterSelect({
  id,
  label,
  valor,
  aoMudar,
  opcoes,
  rotuloTodos = 'Todos',
  className,
}: {
  id: string;
  label: string;
  valor: string | null;
  aoMudar: (valor: string | null) => void;
  opcoes: Array<{ valor: string; rotulo: string }>;
  rotuloTodos?: string;
  className?: string;
}) {
  return (
    <Field id={id} label={label} className={cn('min-w-44', className)}>
      {(props) => (
        <Select value={valor ?? TODOS} onValueChange={(proximo) => aoMudar(proximo === TODOS ? null : proximo)}>
          <SelectTrigger id={props.id} aria-describedby={props['aria-describedby']}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>{rotuloTodos}</SelectItem>
            {opcoes.map((opcao) => (
              <SelectItem key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  );
}

/**
 * Seleção múltipla em chips (`aria-pressed`) — para poucos valores fixos, como
 * os quatro status. Um `Select` múltiplo esconderia o que está ativo atrás de
 * um clique; aqui o estado do filtro é visível na própria barra.
 */
export function FilterChips<T extends string>({
  id,
  label,
  valores,
  aoMudar,
  opcoes,
  className,
  compacto = false,
}: {
  id: string;
  label: string;
  valores: T[];
  aoMudar: (valores: T[]) => void;
  opcoes: Array<{ valor: T; rotulo: string; icone?: React.ReactNode }>;
  className?: string;
  /** Chips mais baixos, para filtros em coluna lateral. */
  compacto?: boolean;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)} role="group" aria-labelledby={`${id}-rotulo`}>
      <span id={`${id}-rotulo`} className="text-label text-on-surface">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {opcoes.map((opcao) => {
          const ativo = valores.includes(opcao.valor);
          return (
            <button
              key={opcao.valor}
              type="button"
              aria-pressed={ativo}
              onClick={() =>
                aoMudar(ativo ? valores.filter((valor) => valor !== opcao.valor) : [...valores, opcao.valor])
              }
              className={cn(
                'inline-flex items-center gap-1.5 rounded-input border text-label transition-colors duration-fast ease-standard [&_svg]:size-3.5',
                compacto ? 'h-8 px-2.5' : 'h-10 px-3',
                ativo
                  ? 'border-primary-border bg-primary-wash text-primary'
                  : 'border-border-strong bg-bg-surface font-normal text-on-surface hover:border-primary',
              )}
            >
              {opcao.icone}
              {opcao.rotulo}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Seleção múltipla em LISTA vertical: uma linha de largura total por opção
 * (ícone + rótulo + caixa de seleção), `aria-pressed`. Nada quebra nem é
 * cortado em coluna estreita — ao contrário dos chips, que viram duas linhas.
 */
export function FilterLista<T extends string>({
  id,
  label,
  valores,
  aoMudar,
  opcoes,
  className,
}: {
  id: string;
  label: string;
  valores: T[];
  aoMudar: (valores: T[]) => void;
  opcoes: Array<{ valor: T; rotulo: string; icone?: React.ReactNode }>;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)} role="group" aria-labelledby={`${id}-rotulo`}>
      <span id={`${id}-rotulo`} className="text-label text-on-surface">
        {label}
      </span>
      <ul className="flex flex-col overflow-hidden rounded-input border border-border-strong bg-bg-surface">
        {opcoes.map((opcao) => {
          const ativo = valores.includes(opcao.valor);
          return (
            <li key={opcao.valor} className="border-b border-border-subtle last:border-b-0">
              <button
                type="button"
                aria-pressed={ativo}
                onClick={() => aoMudar(ativo ? valores.filter((valor) => valor !== opcao.valor) : [...valores, opcao.valor])}
                className={cn(
                  'flex h-9 w-full items-center gap-2.5 px-3 text-left text-label transition-colors duration-fast ease-standard [&_svg]:size-4 [&_svg]:shrink-0',
                  ativo ? 'bg-primary-wash font-bold text-primary' : 'font-normal text-on-surface hover:bg-bg-subtle',
                )}
              >
                {opcao.icone}
                <span className="min-w-0 flex-1 truncate">{opcao.rotulo}</span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-4 items-center justify-center rounded-[3px] border',
                    ativo ? 'border-primary bg-primary text-on-primary' : 'border-border-strong bg-bg-surface',
                  )}
                >
                  {ativo ? <Check className="!size-3" strokeWidth={3} /> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Botão de limpar filtros: só aparece quando há filtro ativo. */
export function FilterLimpar({ ativo, aoLimpar, quantidade }: { ativo: boolean; aoLimpar: () => void; quantidade: number }) {
  if (!ativo) return null;
  return (
    <Button variant="ghost" size="sm" onClick={aoLimpar} className="mb-0.5 h-10">
      <X aria-hidden="true" />
      Limpar {quantidade === 1 ? '1 filtro' : `${quantidade} filtros`}
    </Button>
  );
}
